import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { Clock, Users, Gavel, Briefcase, ReceiptText, Package, Boxes, Wrench, Building2, Star, Rocket, ArrowRight } from "lucide-react";
import { LocationPin } from "./LocationPin";
import { Avatar, Rating } from "./ui/kit";
import { SaveButton } from "./SaveButton";
import { api, type PostOwnership, type PostingIdentity } from "../lib/api";
import { PostCardActions } from "./OwnerPostMenu";
import { peso, type Person, type Service, type Company, type Job, type Project, type Rfq } from "../data/mock";
import { formatRelativeTime } from "../lib/relativeTime";
import { itemTypeLabel, itemPostingLabel, BUSINESS_OPERATING_STATUSES } from "../data/itemTypes";
import { saleUnitLabel } from "../lib/itemUnits";
import { formatCompactCardPhp, formatCompactCardPhpText, formatCompactPhpText } from "../lib/money";
import { AccountVerificationBadge } from "./AccountVerificationBadge";
import { accountVerificationFrom } from "../lib/accountVerification";
import { isNativeApp } from "../lib/platform";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { companyCoverImage } from "../lib/companyCover";

import { RfqCover } from "./RfqCover";
import { RfqTypeBadge, rfqTypeLabelKey } from "./RfqTypeBadge";

const saveChip = "absolute top-3 right-3 z-10 h-8 w-8";

/** Admin reuses listing presentation without member purchase, Save or owner actions. */
type CardPresentation = { showMemberActions?: boolean; footerAccessory?: ReactNode };

/** [marketplace.poster-presentation](../../../docs/product/marketplace.md#marketplace-poster-presentation): service and item rows share compact ratings; unrated listings show no score. */
function CompactCardRating({ value, count }: { value: number; count: number }) {
  if (!(count > 0)) return null;
  return <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-navy sm:text-xs">
    <Star aria-hidden="true" className="h-3.5 w-3.5 fill-brand-gold text-brand-gold" />
    <b>{value.toFixed(1)}</b><span className="text-mut">({count})</span>
  </span>;
}

/** Marketplace cards navigate as one large target; nested links and buttons keep their own actions. */
function MarketplaceCard({ href, children, className = "" }: { href: string; children: ReactNode; className?: string }) {
  const [, navigate] = useLocation();
  const isNestedControl = (target: EventTarget | null) => target instanceof Element && Boolean(target.closest("a, button, input, select, textarea, [role='button']"));

  return (
    <article
      className={`card relative h-full cursor-pointer transition-all duration-150 hover:-translate-y-0.5 hover:border-navy/30 hover:shadow-lift ${className}`}
      onClick={(event) => { if (!isNestedControl(event.target)) navigate(href); }}
    >
      {children}
    </article>
  );
}

/**
 * [marketplace.sorts-and-ratings](../../../docs/product/marketplace.md#marketplace-sorts-and-ratings): "Featured" ribbon for paid-boosted listings. Browse decoration
 * sets `featured=true` without overriding the selected sort; this badge marks them.
 * Gold accent, top-left, out of the way of the save button (top-right).
 */
export function FeaturedBadge({ on }: { on?: boolean }) {
  const { t } = useI18n();
  if (!on) return null;
  return (
    <span className="absolute top-3 left-3 z-10 chip bg-brand-gold text-white shadow-sm inline-flex items-center gap-1">
      <Rocket className="w-3 h-3" />{t("cards.featured")}
    </span>
  );
}

/**
 * v3.9.12 — every marketplace post shows a cover image. When a post has an
 * uploaded photo we render it; otherwise we render a branded placeholder so no
 * card ever looks empty (older posts predate the required-photo rule).
 * v3.9.17 — also fall back to the placeholder when the image URL fails to load
 * (e.g. an old post whose stored bytes are gone), so a broken <img> never shows.
 */
export function CoverArt({ src, alt, Icon, fit = "cover" }: { src?: string | null; alt: string; Icon: any; fit?: "cover" | "contain" }) {
  const [failed, setFailed] = useState(false);
  const resolved = api.apiAssetUrl(src);
  if (resolved && !failed) {
    return <img src={resolved} alt={alt} className={`w-full h-full ${fit === "contain" ? "object-contain" : "object-cover"}`} loading="lazy" onError={() => setFailed(true)} />;
  }
  return (
    <div className="w-full h-full grid place-items-center bg-gradient-to-br from-navy to-navy-600 text-white/90">
      <div className="flex flex-col items-center gap-1.5">
        <Icon className="w-9 h-9" strokeWidth={1.4} />
        <span className="text-[11px] font-bold tracking-[0.15em]">KONTRATA<span className="text-brand-gold">PH</span></span>
      </div>
    </div>
  );
}

/** Full-bleed 4:3 cover at the top of a padded card, linking to the detail page. */
function CardCover({ href, src, alt, Icon, imageClassName = "aspect-[4/3]" }: { href: string; src?: string | null; alt: string; Icon: any; imageClassName?: string }) {
  return (
    <Link href={href} className="block -mx-4 -mt-4 mb-1">
      <div className={`${imageClassName} overflow-hidden rounded-t-xl bg-navy-50`}><CoverArt src={src} alt={alt} Icon={Icon} /></div>
    </Link>
  );
}

/** Show listing tags in native grid cards and summarize any remaining tags. */
function CardTagRow({ tags }: { tags: string[] }) {
  if (!isNativeApp() || tags.length === 0) return null;
  const visibleTags = tags.slice(0, 2);
  const remainingCount = tags.length - visibleTags.length;

  return (
    <div className="flex h-7 min-w-0 flex-nowrap items-center gap-1.5 overflow-hidden whitespace-nowrap" aria-label="Skills">
      {visibleTags.map((tag, index) => <span key={`${tag}-${index}`} className="min-w-0 flex-1">
        <span title={tag} className="inline-flex min-w-0 max-w-full items-center overflow-hidden rounded-full bg-navy-50 px-2.5 py-1 text-xs font-semibold text-navy-600"><span className="min-w-0 truncate">{tag}</span></span>
      </span>)}
      {remainingCount > 0 && <span className="shrink-0 rounded-full bg-navy-50 px-2.5 py-1 text-xs font-semibold text-navy-600" title={`${remainingCount} more skills`}>+{remainingCount} more</span>}
    </div>
  );
}

function JobseekerSkillChips({ skills, limit = 2 }: { skills: string[]; limit?: number }) {
  if (!isNativeApp() || skills.length === 0) return null;
  const visibleSkills = skills.slice(0, limit);
  const remainingCount = skills.length - visibleSkills.length;
  return (
    <div className="flex h-7 min-w-0 flex-nowrap items-center gap-1.5 overflow-hidden whitespace-nowrap" aria-label="Skills">
      {visibleSkills.map((skill, index) => <span key={`${skill}-${index}`} title={skill} className="min-w-0 flex-1 truncate rounded-lg bg-navy-50 px-2.5 py-1 text-xs font-semibold text-navy-600">{skill}</span>)}
      {remainingCount > 0 && <span className="shrink-0 rounded-lg bg-paper px-2.5 py-1 text-xs font-semibold text-mut" title={`${remainingCount} more skills`}>+{remainingCount} more</span>}
    </div>
  );
}

const listSkillChipClass = "shrink-0 rounded-lg bg-navy-50 px-2.5 py-1 text-xs font-semibold text-navy-600";

function countFittingListSkills(row: HTMLElement, measure: HTMLElement): number {
  const widths = Array.from(measure.querySelectorAll<HTMLElement>("[data-skill-width]"), (chip) => chip.offsetWidth);
  const more = measure.querySelector<HTMLElement>("[data-more-width]");
  if (!more) return 0;
  const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
  const prefixWidths = [0];
  for (const width of widths) prefixWidths.push(prefixWidths[prefixWidths.length - 1] + width);

  for (let shown = widths.length; shown >= 0; shown--) {
    const remaining = widths.length - shown;
    if (remaining > 0) more.textContent = `+${remaining} more`;
    const requiredWidth = prefixWidths[shown] + gap * Math.max(0, shown - 1)
      + (remaining > 0 ? more.offsetWidth + (shown > 0 ? gap : 0) : 0);
    if (requiredWidth <= row.clientWidth) return shown;
  }
  return 0;
}

/** Fit List Mode skills in one row while reserving space for the remaining count. */
function JobseekerListSkillChips({ skills }: { skills: string[] }) {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(0);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure || skills.length === 0) return;

    const updateVisibleCount = () => setVisibleCount(countFittingListSkills(row, measure));

    updateVisibleCount();
    const observer = new ResizeObserver(updateVisibleCount);
    observer.observe(row);
    return () => observer.disconnect();
  }, [skills]);

  if (skills.length === 0) return null;
  return (
    <div className="relative min-w-0 overflow-hidden">
      <div ref={rowRef} className="flex min-w-0 gap-1.5 overflow-hidden whitespace-nowrap" aria-label="Skills">
        {skills.slice(0, visibleCount).map((skill, index) => <span key={`${skill}-${index}`} className={listSkillChipClass}>{skill}</span>)}
        {visibleCount < skills.length && <span className={listSkillChipClass}>+{skills.length - visibleCount} more</span>}
      </div>
      <div ref={measureRef} className="pointer-events-none invisible absolute left-0 top-0 flex gap-1.5 whitespace-nowrap" aria-hidden="true">
        {skills.map((skill, index) => <span key={`${skill}-${index}`} data-skill-width className={listSkillChipClass}>{skill}</span>)}
        <span data-more-width className={listSkillChipClass}>+{skills.length} more</span>
      </div>
    </div>
  );
}

function JobseekerCardCover() {
  return (
    <div className="relative h-28 overflow-hidden bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100">
    </div>
  );
}

/** Placeholder follows the same header, details, and action layout as a loaded card. */
export function JobseekerCardSkeleton({ list = false }: { list?: boolean } = {}) {
  if (list) {
    return (
      <div className="card flex flex-col gap-4 p-4 motion-safe:animate-pulse sm:flex-row sm:items-center sm:p-5" aria-hidden="true">
        <div className="h-20 w-20 shrink-0 rounded-xl bg-slate-200 sm:h-24 sm:w-24" />
        <div className="min-w-0 flex-1 space-y-3">
          <div className="h-5 w-2/3 rounded-full bg-slate-200" />
          <div className="h-3 w-1/2 rounded-full bg-slate-100" />
          <div className="h-3 w-3/5 rounded-full bg-slate-100" />
          <div className="flex gap-2"><span className="h-6 w-20 rounded-full bg-slate-100" /><span className="h-6 w-16 rounded-full bg-slate-100" /></div>
        </div>
        <div className="h-14 border-t border-line pt-3 sm:h-24 sm:w-36 sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0">
          <div className="h-3 w-24 rounded-full bg-slate-100" />
        </div>
      </div>
    );
  }
  return (
    <div className="card flex flex-col self-start overflow-hidden" aria-hidden="true">
      <div className="flex min-h-28 items-center gap-3 bg-[#1a3e60] px-4 py-3 motion-safe:animate-pulse">
        <div className="h-20 w-20 shrink-0 rounded-xl bg-white/70" />
        <div className="min-w-0 flex-1 space-y-2 pr-10">
          <div className="h-4 w-full rounded-full bg-white/40" />
          <div className="h-3 w-3/4 rounded-full bg-white/25" />
        </div>
      </div>
      <div className="flex flex-col gap-3 p-4 motion-safe:animate-pulse">
        <div className="rounded-xl bg-slate-100 p-3"><div className="h-3 w-2/3 rounded-full bg-slate-200" /></div>
        <div className="flex gap-2"><span className="h-6 w-16 rounded-lg bg-slate-100" /><span className="h-6 w-20 rounded-lg bg-slate-100" /></div>
        <div className="grid grid-cols-2 items-center gap-2 pt-1"><div className="col-start-2 h-3 w-3/4 justify-self-center rounded-full bg-slate-100" /></div>
      </div>
    </div>
  );
}

export function MarketplaceListCardSkeleton() {
  return (
    <div className="card grid grid-cols-[80px_minmax(0,1fr)] gap-4 p-4 motion-safe:animate-pulse sm:grid-cols-[96px_minmax(0,1fr)_150px] sm:items-center sm:gap-5 sm:p-5" aria-hidden="true">
      <div className="h-20 w-20 rounded-xl bg-slate-200 sm:h-24 sm:w-24" />
      <div className="min-w-0 space-y-3">
        <div className="h-5 w-3/4 rounded-full bg-slate-200" />
        <div className="h-3 w-1/2 rounded-full bg-slate-100" />
        <div className="h-3 w-2/3 rounded-full bg-slate-100" />
        <div className="flex gap-2"><span className="h-6 w-20 rounded-lg bg-slate-100" /><span className="h-6 w-16 rounded-lg bg-slate-100" /></div>
      </div>
      <div className="col-span-2 flex items-center justify-between gap-3 border-t border-line pt-3 sm:col-span-1 sm:h-24 sm:flex-col sm:items-end sm:justify-end sm:border-t-0 sm:pt-0">
        <div className="h-4 w-24 rounded-full bg-slate-200" /><div className="h-3 w-20 rounded-full bg-slate-100" />
      </div>
    </div>
  );
}

export function ServiceCardSkeleton({ list = false }: { list?: boolean } = {}) {
  if (list) return <MarketplaceListCardSkeleton />;
  return (
    <div className="card self-start overflow-hidden motion-safe:animate-pulse" aria-hidden="true">
      <div className="aspect-square bg-slate-200" />
      <div className="flex flex-col gap-2.5 p-3.5">
        <div className="h-4 w-4/5 rounded-full bg-slate-200" />
        <div className="h-3 w-1/2 rounded-full bg-slate-100" />
        <div className="flex items-center justify-between gap-3"><div className="h-3 w-2/5 rounded-full bg-slate-100" /><div className="h-3 w-16 rounded-full bg-slate-100" /></div>
        <div className="flex gap-2"><span className="h-7 w-16 rounded-lg bg-slate-100" /><span className="h-7 w-20 rounded-lg bg-slate-100" /></div>
        <div className="flex items-center justify-between border-t border-line pt-2.5"><div className="space-y-1.5"><div className="h-3 w-16 rounded-full bg-slate-100" /><div className="h-4 w-14 rounded-full bg-slate-200" /></div><div className="h-9 w-28 rounded-lg bg-slate-200" /></div>
      </div>
    </div>
  );
}

export function CompanyCardSkeleton({ list = false }: { list?: boolean } = {}) {
  if (list) return <MarketplaceListCardSkeleton />;
  if (!isNativeApp()) return (
    <div className="card self-start overflow-hidden motion-safe:animate-pulse" aria-hidden="true">
      <div className="h-32 bg-[#1a3e60] sm:h-[135px]" />
      <div className="flex flex-col gap-2.5 p-3 sm:hidden">
        <div className="h-4 w-4/5 rounded-full bg-slate-200" />
        <div className="h-3 w-2/3 rounded-full bg-slate-100" />
        <div className="h-3 w-1/2 rounded-full bg-slate-100" />
      </div>
      <div className="hidden min-w-0 flex-col items-center sm:flex">
        <div className="-mt-[67.2px] h-[134.4px] w-[134.4px] rounded-full border-4 border-white bg-slate-200 shadow-lg" />
        <div className="flex w-full flex-col items-center px-4 pb-5 pt-3">
          <div className="h-4 w-2/3 rounded-full bg-slate-200" />
          <div className="mt-2 h-3 w-1/2 rounded-full bg-slate-100" />
          <div className="mt-5 h-3 w-3/5 rounded-full bg-slate-100" />
          <div className="mt-5 flex w-full justify-center">
            <div className="h-3 w-1/3 rounded-full bg-slate-100" />
          </div>
        </div>
      </div>
    </div>
  );
  return (
    <div className="card self-start overflow-hidden motion-safe:animate-pulse" aria-hidden="true">
      <div className="flex min-h-28 items-center gap-3 bg-[#1a3e60] px-4 py-3">
        <div className="h-[86px] w-[86px] shrink-0 rounded-xl bg-white/70" />
        <div className="min-w-0 flex-1 space-y-2 pr-10"><div className="h-4 w-full rounded-full bg-white/40" /><div className="h-3 w-3/4 rounded-full bg-white/25" /></div>
      </div>
      <div className="flex flex-col gap-3 p-4">
        <div className="rounded-xl bg-paper p-3"><div className="mx-auto h-3 w-2/3 rounded-full bg-slate-200" /></div>
        <div className="flex gap-2"><span className="h-7 w-16 rounded-lg bg-slate-100" /><span className="h-7 w-20 rounded-lg bg-slate-100" /></div>
        <div className="flex items-center justify-between pt-1"><div className="h-3 w-20 rounded-full bg-slate-100" /><div className="h-3 w-24 rounded-full bg-slate-100" /></div>
      </div>
    </div>
  );
}

/** Older marketplace listings can contain a full address where newer records store only the city. */
function cityFromListingLocation(location: string): string {
  const parts = location.split(",").map((part) => part.trim()).filter(Boolean);
  return parts.find((part) => /\b(city|municipality)\b/i.test(part)) || parts[0] || location;
}

export function PersonCard({ p }: { p: Person }) {
  const { t } = useI18n();
  const verification = accountVerificationFrom(p);
  return (
    <MarketplaceCard href={`/p/${p.id}`} className="h-full overflow-hidden p-0">
      <FeaturedBadge on={(p as any).featured} />
      <SaveButton kind="person" refId={p.id} imageOverlay className="absolute right-3 top-3 z-10 h-8 w-8" />
      <Link href={`/p/${p.id}`} aria-label={`View ${p.name}'s résumé`} className="flex h-full min-h-[365px] flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold">
        <JobseekerCardCover />
        <div className="flex flex-1 flex-col gap-3 p-4">
          <div className="-mt-11 w-fit rounded-full ring-4 ring-white"><Avatar initials={p.initials} size={58} /></div>
          <div className="min-w-0">
            <h3 className="flex items-center gap-1.5 text-lg font-bold leading-tight text-navy" title={p.name}><span className="line-clamp-2 min-w-0">{p.name}</span><AccountVerificationBadge {...verification} /></h3>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <p className="line-clamp-2 text-sm text-mut">{p.title}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            {p.available && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-greendark"><span className="h-2 w-2 rounded-full bg-brand-green" />{t("cards.availableNow")}</span>}
            {p.reviews > 0 && <Rating value={p.rating} count={p.reviews} />}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-mut">
            <span>{cityFromListingLocation(p.city)}</span><span>{p.years} {t("cards.yearsExperience")}</span>
          </div>
          <JobseekerSkillChips skills={p.skills ?? []} />
          <div className="mt-auto border-t border-line pt-3">
            <div className="min-w-0"><div className="text-base font-bold text-navy"><span className="sm:hidden">{formatCompactCardPhpText(p.rate)}</span><span className="hidden sm:inline">{formatCompactPhpText(p.rate)}</span></div></div>
          </div>
        </div>
      </Link>
    </MarketplaceCard>
  );
}

export type JobseekerCardProfile = {
  id: number;
  name: string;
  initials: string;
  headline: string | null;
  city: string | null;
  bio: string | null;
  skills: string[];
  /** Legacy marketplace responses used `verified` for approved identity checks. */
  verified: boolean;
  identityVerified?: boolean;
  verifiedProfessions?: string[];
  professionalCount?: number;
  professionalValidity?: "valid" | "none";
  profileImageUrl: string | null;
  bannerImageUrl?: string | null;
  yearsExperience?: number | null;
  expectedRate?: number | null;
  rating: number;
  reviews: number;
  availabilityStatus: "available";
};

/** Web grid headers retain their navy fallback if the public cover cannot load. */
function JobseekerGridCover({ src }: { src?: string | null }) {
  const [failedCoverUrl, setFailedCoverUrl] = useState<string | null>(null);
  const coverUrl = api.apiAssetUrl(src);
  if (!coverUrl || coverUrl === failedCoverUrl) return null;
  return <img src={coverUrl} alt="" className="h-full w-full object-cover" loading="lazy" onError={() => setFailedCoverUrl(coverUrl)} />;
}

/** One member's discoverable public profile. */
export function PublicProfileCard({ profile, showMemberActions = true, footerAccessory }: { profile: JobseekerCardProfile } & CardPresentation) {
  const { t } = useI18n();
  const [failedPhotoUrl, setFailedPhotoUrl] = useState<string | null>(null);
  const photoUrl = api.apiAssetUrl(profile.profileImageUrl);
  const showPhoto = Boolean(photoUrl && photoUrl !== failedPhotoUrl);
  const hasRating = profile.rating > 0 && profile.reviews > 0;
  const verifiedProfessions = profile.verifiedProfessions ?? [];
  const nativeApp = isNativeApp();

  return (
    <MarketplaceCard href={`/u/${profile.id}`} className="h-full overflow-hidden p-0">
      {showMemberActions && <SaveButton kind="profile" refId={String(profile.id)} imageOverlay className="absolute right-3 top-3 z-10 h-9 w-9" />}
      <Link href={`/u/${profile.id}`} aria-label={`View ${profile.name}'s public profile`} className="flex h-full flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold">
        <div className="relative h-32 shrink-0 overflow-hidden bg-[#1a3e60] sm:hidden">
          {showPhoto ? (
            <img src={photoUrl || undefined} alt="" className="h-full w-full object-cover object-center" loading="lazy" onError={() => setFailedPhotoUrl(photoUrl)} />
          ) : <div className="grid h-full w-full place-items-center text-4xl font-bold text-white" aria-hidden="true">{profile.initials}</div>}
        </div>
        <div className="flex flex-1 flex-col gap-2.5 p-3 sm:hidden">
          <div className="flex min-w-0 items-center gap-1.5">
            <h3 className="min-w-0 truncate text-sm font-bold leading-tight text-navy" title={profile.name}>{profile.name}</h3>
            <AccountVerificationBadge identityVerified={profile.identityVerified ?? profile.verified} verifiedProfessions={verifiedProfessions} />
          </div>
          {profile.headline && <div className="flex min-w-0 items-center gap-1.5">
            {profile.headline && <p className="min-w-0 truncate text-xs text-mut" title={profile.headline}>{profile.headline}</p>}
          </div>}
          {(profile.city || profile.yearsExperience != null) && <div className="flex min-w-0 flex-wrap items-center gap-x-2 text-xs text-mut">
            {profile.city && <span className="truncate" title={profile.city}>{profile.city}</span>}
            {profile.yearsExperience != null && <span className="shrink-0">{profile.yearsExperience} {t("cards.yearsExperience")}</span>}
          </div>}
          {hasRating && <div><Rating value={profile.rating} count={profile.reviews} /></div>}
        </div>
        {nativeApp ? <><div className="hidden min-h-28 shrink-0 items-center gap-3 overflow-hidden bg-[#1a3e60] px-4 py-3 sm:flex">
          {showPhoto ? (
            <img src={photoUrl || undefined} alt="" className="h-20 w-20 shrink-0 rounded-xl border-[3px] border-white/70 bg-navy-50 object-cover" loading="lazy" onError={() => setFailedPhotoUrl(photoUrl)} />
          ) : <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border-[3px] border-white/70 bg-navy-50 text-[28px] font-bold text-navy">{profile.initials}</div>}
          <div className="min-w-0 flex-1 pr-10 text-left text-white">
            <div className="flex items-center justify-start gap-1.5">
              <h3 className="min-w-0 truncate text-sm font-bold leading-tight" title={profile.name}>{profile.name}</h3>
              <AccountVerificationBadge identityVerified={profile.identityVerified ?? profile.verified} verifiedProfessions={verifiedProfessions} />
            </div>
            <div className="mt-1 flex min-w-0 items-center justify-start gap-1.5">
              {profile.headline && <p className="min-w-0 truncate text-xs text-white/85">{profile.headline}</p>}
            </div>
          </div>
        </div>
        <div className="hidden flex-1 flex-col gap-3 p-4 sm:flex">
          <div className="rounded-xl bg-paper p-3">
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-center text-xs text-mut">
              {profile.city && <span>{profile.city}</span>}
              {profile.yearsExperience != null && <span>{profile.yearsExperience} {t("cards.yearsExperience")}</span>}
              {hasRating && <span className="shrink-0"><Rating value={profile.rating} count={profile.reviews} /></span>}
            </div>
          </div>
          <JobseekerSkillChips skills={profile.skills} />
          <div className="mt-auto flex justify-end pt-1">
            <span className="inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-brand-greendark"><span className="h-2 w-2 shrink-0 rounded-full bg-brand-green" />{t("cards.availableNow")}</span>
          </div>
        </div></> : <WebJobseekerGridCardContent profile={profile} hasRating={hasRating} verifiedProfessions={verifiedProfessions} />}
        {footerAccessory && <div className="mt-auto flex justify-end border-t border-line px-3 py-2 sm:px-4">{footerAccessory}</div>}
      </Link>
    </MarketplaceCard>
  );
}

/** Browser grid presentation; the mobile and native layouts stay on their existing paths. */
function WebJobseekerGridCardContent({ profile, hasRating, verifiedProfessions }: {
  profile: JobseekerCardProfile;
  hasRating: boolean;
  verifiedProfessions: string[];
}) {
  const { t } = useI18n();
  const [failedPhotoUrl, setFailedPhotoUrl] = useState<string | null>(null);
  const photoUrl = api.apiAssetUrl(profile.profileImageUrl);
  const showPhoto = Boolean(photoUrl && photoUrl !== failedPhotoUrl);

  return <div className="hidden min-w-0 flex-1 flex-col sm:flex">
    <div className="relative h-[135px] shrink-0 overflow-hidden bg-[#1a3e60]"><JobseekerGridCover src={profile.bannerImageUrl} /></div>
    {showPhoto ? (
      <img src={photoUrl || undefined} alt="" className="relative z-[1] mx-auto -mt-[67.2px] h-[134.4px] w-[134.4px] shrink-0 rounded-full border-4 border-white bg-navy-50 object-cover shadow-lg" loading="lazy" onError={() => setFailedPhotoUrl(photoUrl)} />
    ) : (
      <div className="relative z-[1] mx-auto -mt-[67.2px] flex h-[134.4px] w-[134.4px] shrink-0 items-center justify-center rounded-full border-4 border-white bg-navy-50 text-3xl font-bold text-navy shadow-lg" aria-hidden="true">{profile.initials}</div>
    )}
    <div className="flex min-w-0 flex-1 flex-col items-center px-4 pb-5 pt-3 text-center">
      <div className="flex max-w-full items-center justify-center gap-1.5">
        <h3 className="min-w-0 truncate text-base font-bold leading-tight text-navy" title={profile.name}>{profile.name}</h3>
        <AccountVerificationBadge identityVerified={profile.identityVerified ?? profile.verified} verifiedProfessions={verifiedProfessions} />
      </div>
      {profile.headline && <div className="mt-1 flex max-w-full flex-wrap items-center justify-center gap-1.5 text-sm text-mut">
        {profile.headline && <span className="min-w-0 truncate" title={profile.headline}>{profile.headline}</span>}
      </div>}
      <div className="mt-5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-mut">
        {profile.city && <span className="inline-flex items-center gap-1 whitespace-nowrap"><LocationPin className="h-3.5 w-3.5 shrink-0" />{profile.city}</span>}
        {profile.city && <span className="text-slate-300" aria-hidden="true">|</span>}
        {profile.yearsExperience != null && <span className="whitespace-nowrap">{profile.yearsExperience} {t("cards.yearsExperience")}</span>}
        {profile.yearsExperience != null && <span className="text-slate-300" aria-hidden="true">|</span>}
        <span className="shrink-0">{hasRating ? <Rating value={profile.rating} count={profile.reviews} /> : t("cards.noRatings")}</span>
      </div>
    </div>
  </div>;
}

/** Compact Jobseeker result for List Mode; Grid Mode uses the separate profile tile. */
export function PublicProfileListCard({ profile }: { profile: JobseekerCardProfile }) {
  const { t } = useI18n();
  const [failedPhotoUrl, setFailedPhotoUrl] = useState<string | null>(null);
  const verifiedProfessions = profile.verifiedProfessions ?? [];
  const photoUrl = api.apiAssetUrl(profile.profileImageUrl);
  const showPhoto = Boolean(photoUrl && failedPhotoUrl !== photoUrl);
  return (
    <MarketplaceCard href={`/u/${profile.id}`} className="overflow-hidden p-0">
      <SaveButton kind="profile" refId={String(profile.id)} className="absolute right-4 top-4 z-10 h-9 w-9" />
      <Link href={`/u/${profile.id}`} aria-label={`View ${profile.name}'s public profile`} className="grid min-w-0 grid-cols-[80px_minmax(0,1fr)] gap-4 p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold sm:grid-cols-[96px_minmax(0,1fr)_150px] sm:items-center sm:gap-5 sm:p-5">
        {showPhoto ? (
          <img src={photoUrl || undefined} alt="" className="h-20 w-20 rounded-xl bg-navy-50 object-cover sm:h-24 sm:w-24" loading="lazy" onError={() => setFailedPhotoUrl(photoUrl)} />
        ) : (
          <div className="grid h-20 w-20 place-items-center rounded-xl bg-navy-50 text-2xl font-bold text-navy sm:h-24 sm:w-24" aria-hidden="true">{profile.initials}</div>
        )}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 pr-10 sm:pr-0">
            <h3 className="text-base font-bold leading-tight text-navy sm:text-lg">{profile.name}</h3>
            <AccountVerificationBadge identityVerified={profile.identityVerified ?? profile.verified} verifiedProfessions={verifiedProfessions} />
          </div>
          {profile.headline && <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {profile.headline && <p className="text-sm text-mut">{profile.headline}</p>}
          </div>}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-mut">
            {profile.city && <span className="inline-flex items-center gap-1"><LocationPin className="hidden h-3.5 w-3.5 shrink-0 sm:inline" />{profile.city}</span>}
            <span className="shrink-0">{profile.rating > 0 && profile.reviews > 0 ? <Rating value={profile.rating} count={profile.reviews} /> : t("cards.noRatings")}</span>
          </div>
          <div className="mt-3 hidden sm:block"><JobseekerListSkillChips skills={profile.skills} /></div>
        </div>
        <div className="hidden items-end justify-end sm:col-span-1 sm:flex sm:min-h-24 sm:py-1 sm:pl-4">
        </div>
      </Link>
    </MarketplaceCard>
  );
}

// [verification.public-signals](../../../docs/product/profiles-and-verification.md#verification-public-signals): the blue Professional badge also represents identity approval.
// Show green only for identity-only providers; companies never inherit personal badges.
function ServiceProviderLine({ s, className = "text-xs" }: { s: Service; className?: string }) {
  return <ListingAuthorLine postingIdentity={s.postingIdentity} name={s.by} identityVerified={!s.company && s.identityVerified === true}
    verifiedProfessions={s.company ? [] : s.verifiedProfessions} className={className} />;
}

// [verification.public-signals](../../../docs/product/profiles-and-verification.md#verification-public-signals): Business authors use company links without badges; Personal authors retain one combined badge.
function ListingAuthorLine({ postingIdentity, name, identityVerified, verifiedProfessions = [], className = "text-xs" }: {
  postingIdentity?: PostingIdentity; name: string; identityVerified?: boolean; verifiedProfessions?: readonly string[]; className?: string;
}) {
  return <div className={`flex min-w-0 items-center gap-1.5 text-mut ${className}`}>
    {postingIdentity?.kind === "business" ? postingIdentity.href
      ? <Link href={postingIdentity.href} className="min-w-0 truncate hover:underline" title={postingIdentity.name}>by {postingIdentity.name}</Link>
      : <span className="min-w-0 truncate" title={postingIdentity.name}>by {postingIdentity.name}</span>
      : <><span className="min-w-0 truncate" title={name}>by {name}</span><AccountVerificationBadge identityVerified={identityVerified} verifiedProfessions={verifiedProfessions} /></>}
  </div>;
}

export function ServiceCard({ s, profile = false, showMemberActions = true, footerAccessory }: { s: Service; profile?: boolean } & CardPresentation) {
  const { t } = useI18n();
  const cover = (s as any).coverImage as string | undefined;
  if (profile) return (
    <MarketplaceCard href={`/s/${s.id}`} className="@container h-full overflow-hidden p-0">
      <FeaturedBadge on={(s as any).featured} />
      {showMemberActions && <PostCardActions kind="service" post={s} imageOverlay className="absolute right-4 top-4 z-10 h-9 w-9 @lg:right-7 @lg:top-7 @lg:h-10 @lg:w-10" iconClassName="h-5 w-5 @lg:h-6 @lg:w-6" />}
      <div className="group flex h-full min-w-0 flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold">
        <div className="relative aspect-[5/3] w-full shrink-0 overflow-hidden bg-[#102d52]">
          {cover
            ? <div className="h-full w-full motion-safe:transition-transform motion-safe:duration-300 motion-safe:group-hover:scale-105"><CoverArt src={cover} alt="" Icon={Wrench} /></div>
            : <>
                <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
                  <span className="absolute -left-[28%] -top-[74%] h-[125%] aspect-square rounded-full bg-[#1d416b]/75" />
                  <span className="absolute -left-[34%] top-[55%] h-[115%] aspect-square rounded-full bg-[#0b2547]/70" />
                  <span className="absolute -right-[37%] top-[42%] h-[128%] aspect-square rounded-full bg-[#1d416b]/65" />
                </div>
                <div className="relative flex h-full flex-col items-center justify-center gap-4 text-white/90 @lg:gap-5">
                  <Wrench className="h-12 w-12 @lg:h-16 @lg:w-16" strokeWidth={1.8} />
                  <span className="text-sm font-bold tracking-[0.22em] @lg:text-base">KONTRATA<span className="text-brand-gold">PH</span></span>
                </div>
              </>}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-3 p-4 sm:hidden">
          <div className="flex min-w-0 items-start justify-between gap-3">
            <h3 className="min-w-0 truncate text-lg font-extrabold leading-tight text-navy @lg:text-2xl" title={s.title} aria-label={s.title}><Link href={`/s/${s.id}`} aria-label={`View ${s.title}`} className="hover:underline">{s.title}</Link></h3>
          </div>
          <ServiceProviderLine s={s} className="text-xs sm:hidden" />
          <div className="flex min-w-0 items-center gap-3 text-xs text-mut @lg:gap-6 @lg:text-lg">
            <span className="flex min-w-0 flex-1 items-center gap-2"><LocationPin className="hidden h-5 w-5 shrink-0 text-brand-red sm:block" /><span className="truncate" title={s.city}>{s.city}</span></span>
            {s.postingIdentity?.kind !== "business" && (s.reviews ?? 0) > 0 && <><span className="h-8 w-px shrink-0 bg-line" aria-hidden="true" /><span className="shrink-0"><Rating value={s.rating ?? 0} count={s.reviews ?? 0} /></span></>}
          </div>
          <div className="mt-auto border-t-2 border-line pt-3 @lg:pt-5">
            <div className="text-sm font-extrabold text-navy @lg:text-xl"><span className="sm:hidden">{t("discovery.from")} {formatCompactCardPhp(s.from)}</span><span className="hidden sm:inline">{t("discovery.from")} {peso(s.from)}</span></div>
          </div>
        </div>
        <div className="hidden min-w-0 flex-1 flex-col gap-4 p-4 sm:flex @lg:gap-5 @lg:p-8">
          <div className="flex min-w-0 items-start justify-between gap-3">
            <h3 className="min-w-0 truncate text-lg font-extrabold leading-tight text-navy @lg:text-2xl" title={s.title} aria-label={s.title}><Link href={`/s/${s.id}`} aria-label={`View ${s.title}`} className="hover:underline">{s.title}</Link></h3>
          </div>
          <ServiceProviderLine s={s} className="-mt-2 text-sm @lg:-mt-3 @lg:text-xl" />
          <div className="flex min-w-0 items-center gap-3 text-xs text-mut @lg:gap-6 @lg:text-lg">
            <span className="flex min-w-0 flex-1 items-center gap-2"><LocationPin className="h-5 w-5 shrink-0 text-brand-red" /><span className="truncate" title={s.city}>{s.city}</span></span>
            {s.postingIdentity?.kind !== "business" && (s.reviews ?? 0) > 0 && <><span className="h-8 w-px shrink-0 bg-line" aria-hidden="true" /><span className="shrink-0"><Rating value={s.rating ?? 0} count={s.reviews ?? 0} /></span></>}
          </div>
          <CardTagRow tags={s.tags ?? []} />
          <div className="mt-auto grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] items-end gap-3 border-t-2 border-line pt-4 @lg:gap-5 @lg:pt-8">
            <div className="min-w-0 text-sm leading-5 text-mut @lg:text-xl @lg:leading-6"><span className="block">{t("discovery.startingAt")}</span><span className="block text-xl font-extrabold text-navy @lg:text-3xl">{peso(s.from)}</span></div>
            {showMemberActions && <span className="inline-flex min-w-0 items-center justify-center gap-2 rounded-2xl bg-brand-red px-2 py-3 text-center text-xs font-bold text-white shadow-lg shadow-brand-red/15 @lg:gap-4 @lg:px-5 @lg:py-5 @lg:text-xl">{t("discovery.quote")}<ArrowRight className="h-5 w-5 shrink-0 @lg:h-7 @lg:w-7" /></span>}
          </div>
        </div>
      </div>
    </MarketplaceCard>
  );
  return (
    <MarketplaceCard href={`/s/${s.id}`} className="self-start overflow-hidden p-0">
      <FeaturedBadge on={(s as any).featured} />
      {showMemberActions && <PostCardActions kind="service" post={s} imageOverlay className="absolute right-3 top-3 z-10 h-9 w-9" />}
      <div className="group flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold">
        <div className="aspect-[4/3] w-full shrink-0 overflow-hidden bg-navy-50 sm:aspect-square">
          <div className="h-full w-full motion-safe:transition-transform motion-safe:duration-300 motion-safe:group-hover:scale-105"><CoverArt src={cover} alt="" Icon={Wrench} /></div>
        </div>
        <div className="flex flex-col gap-2.5 p-3.5 sm:hidden">
          <div className="flex min-w-0 items-center gap-1.5">
            <h3 className="min-w-0 truncate text-sm font-bold text-navy" title={s.title} aria-label={s.title}><Link href={`/s/${s.id}`} aria-label={`View ${s.title}`} className="hover:underline">{s.title}</Link></h3>
          </div>
          <ServiceProviderLine s={s} />
          <div className="flex min-w-0 items-center gap-2 text-xs leading-5 text-mut">
            <span className="flex min-w-0 flex-1 items-center gap-1.5"><LocationPin className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 truncate" title={s.city}>{s.city}</span></span>
            {s.postingIdentity?.kind !== "business" && <CompactCardRating value={s.rating ?? 0} count={s.reviews ?? 0} />}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-line pt-2.5">
            {footerAccessory ? <div className="min-w-0"><span className="block text-xs text-mut">{t("discovery.from")}</span><span className="text-sm font-bold text-navy">{formatCompactCardPhp(s.from)}</span></div> : <><span className="text-xs text-mut">{t("discovery.from")}</span><span className="text-sm font-bold text-navy">{formatCompactCardPhp(s.from)}</span></>}
            {footerAccessory}
          </div>
        </div>
        <div className="hidden flex-col gap-2.5 p-3.5 sm:flex">
          <div className="flex min-w-0 items-center gap-1.5">
            <h3 className="min-w-0 truncate text-sm font-bold text-navy" title={s.title} aria-label={s.title}><Link href={`/s/${s.id}`} aria-label={`View ${s.title}`} className="hover:underline">{s.title}</Link></h3>
          </div>
          <ServiceProviderLine s={s} />
          <div className="flex min-w-0 items-center gap-2 text-xs leading-5 text-mut">
            <span className="flex min-w-0 flex-1 items-center gap-1.5"><LocationPin className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 truncate" title={s.city}>{s.city}</span></span>
            {s.postingIdentity?.kind !== "business" && <CompactCardRating value={s.rating ?? 0} count={s.reviews ?? 0} />}
          </div>
          <CardTagRow tags={s.tags ?? []} />
          {/* [marketplace.poster-presentation](../../../docs/product/marketplace.md#marketplace-poster-presentation): desktop price leads the supporting metadata; phone/profile/list typography stays separate. */}
          <div className="flex items-center justify-between gap-2 border-t border-line pt-2.5">
            <div className="min-w-0"><span className="text-xs text-mut">{t("discovery.startingAt")}</span><div className="text-base font-bold text-navy">{peso(s.from)}</div></div>
            {showMemberActions && <span className="shrink-0 rounded-lg bg-brand-red px-3 py-2 text-xs font-semibold text-white">{t("discovery.quote")}</span>}
            {footerAccessory}
          </div>
        </div>
      </div>
    </MarketplaceCard>
  );
}

export function ServiceListCard({ s }: { s: Service }) {
  const { t } = useI18n();
  const cover = (s as any).coverImage as string | undefined;
  return (
    <MarketplaceCard href={`/s/${s.id}`} className="overflow-hidden p-0">
      <FeaturedBadge on={(s as any).featured} />
      <PostCardActions kind="service" post={s} className="absolute right-4 top-4 z-10 h-9 w-9" />
      <div className="grid min-w-0 grid-cols-[80px_minmax(0,1fr)] gap-4 p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold sm:grid-cols-[96px_minmax(0,1fr)_150px] sm:items-center sm:gap-5 sm:p-5">
        <div className="h-20 w-20 overflow-hidden rounded-xl bg-navy-50 sm:h-24 sm:w-24"><CoverArt src={cover} alt="" Icon={Wrench} /></div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 pr-10 sm:pr-0">
            <h3 className="min-w-0 truncate text-lg font-bold leading-tight text-navy" title={s.title} aria-label={s.title}><Link href={`/s/${s.id}`} aria-label={`View ${s.title}`} className="hover:underline">{s.title}</Link></h3>
          </div>
          <ServiceProviderLine s={s} className="mt-1 text-sm" />
          <div className="mt-2 flex min-w-0 items-center gap-2 text-xs leading-5 text-mut"><span className="inline-flex min-w-0 flex-1 items-center gap-1.5"><LocationPin className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 truncate" title={s.city}>{s.city}</span></span>{s.postingIdentity?.kind !== "business" && <CompactCardRating value={s.rating ?? 0} count={s.reviews ?? 0} />}</div>
          <div className="mt-2 flex justify-end border-t border-line pt-2 text-sm font-bold text-navy sm:hidden">{t("discovery.from")} {peso(s.from)}</div>
          <div className="mt-3 hidden sm:block"><JobseekerListSkillChips skills={s.tags ?? []} /></div>
        </div>
        <div className="hidden items-end justify-between gap-3 sm:col-span-1 sm:flex sm:min-h-24 sm:flex-col sm:items-end sm:justify-end sm:border-t-0 sm:py-1 sm:pl-4">
          <span className="text-sm font-bold text-navy">{t("discovery.from")} {peso(s.from)}</span>
          <span className="text-xs font-semibold text-brand-reddark">{t("discovery.quote")} →</span>
        </div>
      </div>
    </MarketplaceCard>
  );
}

function CompanyLogo({ c, large = false, banner = false, spotlight = false }: { c: Company; large?: boolean; banner?: boolean; spotlight?: boolean }) {
  const x = c as any;
  const [failed, setFailed] = useState(false);
  const size = spotlight ? "h-[134.4px] w-[134.4px]" : banner ? "h-full w-full" : large ? "h-20 w-20 sm:h-24 sm:w-24" : "h-20 w-20";
  if (x.logoObject && !failed) {
    return <img src={api.apiAssetUrl(api.companyLogoUrl(c.id)) || undefined} alt="" className={`${size} shrink-0 ${spotlight ? "rounded-full border-4 border-white bg-navy-50 object-contain shadow-lg" : banner ? "object-cover" : "rounded-xl bg-white object-cover"}`} onError={() => setFailed(true)} />;
  }
  return <div className={`${size} grid shrink-0 place-items-center ${spotlight ? "rounded-full border-4 border-white bg-navy-50 text-3xl text-navy shadow-lg" : banner ? "text-5xl text-white" : "rounded-xl bg-navy-50 text-2xl text-navy"} font-bold`} aria-hidden="true">{c.initials}</div>;
}

export function CompanyCard({ c, webGrid = false, showMemberActions = true, footerAccessory }: { c: Company; webGrid?: boolean } & CardPresentation) {
  const { t } = useI18n();
  const x = c as any;
  const roles: string[] = Array.isArray(x.businessRoles) ? x.businessRoles : [];
  const chips = roles.length ? roles : c.tags ?? [];
  const meta = [x.subtype, x.primaryIndustry].filter(Boolean).join(" · ");
  const showWebGrid = webGrid && !isNativeApp();
  return (
    <MarketplaceCard href={`/c/${c.id}`} className="self-start overflow-hidden p-0">
      <FeaturedBadge on={(c as any).featured} />
      {showMemberActions && <PostCardActions kind="company" post={c} imageOverlay className="absolute right-3 top-3 z-10 h-9 w-9" />}
      <Link href={`/c/${c.id}`} aria-label={`View ${c.name}`} className="flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold">
        <div className="h-32 w-full shrink-0 overflow-hidden bg-[#1a3e60] sm:hidden">
          <CompanyLogo c={c} banner />
        </div>
        <div className="flex flex-1 flex-col gap-2.5 p-3 sm:hidden">
          <h3 className="truncate text-sm font-bold leading-tight text-navy" title={c.name}>{c.name}</h3>
          {c.tagline && <p className="truncate text-xs text-mut" title={c.tagline}>{c.tagline}</p>}
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 text-xs text-mut">
            <span className="truncate" title={c.city}>{c.city}</span>
            {c.reviews > 0 && <Rating value={c.rating} count={c.reviews} />}
          </div>
        </div>
        {showWebGrid ? <WebCompanyGridCardContent c={c} coverage={x.serviceCoverage || (c.since ? `${t("cards.since")} ${c.since}` : "")} /> : <><div className="hidden min-h-28 items-center gap-3 bg-[#1a3e60] px-4 py-3 sm:flex">
          <div className="shrink-0 rounded-xl border-[3px] border-white/70"><CompanyLogo c={c} /></div>
          <div className="min-w-0 flex-1 pr-10 text-white">
            <h3 className="min-w-0 truncate text-sm font-bold" title={c.name}>{c.name}</h3>
            <p className="mt-1 truncate text-xs text-white/85" title={c.tagline}>{c.tagline}</p>
          </div>
        </div>
        <div className="hidden flex-col gap-3 p-4 sm:flex">
          <div className="rounded-xl bg-paper p-3 text-center text-xs text-mut">
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1"><span>{c.city}</span>{c.reviews > 0 && <Rating value={c.rating} count={c.reviews} />}</div>
          </div>
          {meta && <p className="truncate text-xs text-navy/70" title={meta}>{meta}</p>}
          <CardTagRow tags={chips} />
          <div className="pt-1 text-center text-xs text-mut">
            <span className="block truncate">{x.serviceCoverage || (c.since ? `${t("cards.since")} ${c.since}` : "")}</span>
          </div>
        </div></>}
        {footerAccessory && <div className="mt-auto flex justify-end border-t border-line px-3 py-2 sm:px-4">{footerAccessory}</div>}
      </Link>
    </MarketplaceCard>
  );
}

/** Companies directory grid on the web; other CompanyCard consumers keep the existing layout. */
function WebCompanyGridCardContent({ c, coverage }: { c: Company; coverage: string }) {
  const [failedCoverUrl, setFailedCoverUrl] = useState<string | null>(null);
  // Saved profile covers use the public media endpoint; the object revision refreshes replacements.
  const coverUrl = api.apiAssetUrl(c.coverObject
    ? `${api.companyCoverUrl(c.id)}?v=${encodeURIComponent(c.coverObject)}`
    : c.coverImage || companyCoverImage(c.primaryIndustry));
  const showCover = Boolean(coverUrl && coverUrl !== failedCoverUrl);

  return <div className="hidden min-w-0 flex-col sm:flex">
    <div className="h-[135px] shrink-0 bg-[#1a3e60]">
      {showCover && <img src={coverUrl || undefined} alt="" className="h-full w-full object-cover" loading="lazy" onError={() => setFailedCoverUrl(coverUrl)} />}
    </div>
    <div className="relative z-[1] mx-auto -mt-[67.2px]"><CompanyLogo c={c} spotlight /></div>
    <div className="flex min-w-0 flex-col items-center px-4 pb-5 pt-3 text-center">
      <h3 className="max-w-full truncate text-base font-bold leading-tight text-navy" title={c.name}>{c.name}</h3>
      {c.tagline && <p className="mt-1 max-w-full truncate text-sm text-mut" title={c.tagline}>{c.tagline}</p>}
      <div className="mt-5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-mut">
        <span className="inline-flex items-center gap-1 whitespace-nowrap"><LocationPin className="h-3.5 w-3.5 shrink-0" />{c.city}</span>
        {c.reviews > 0 && <><span className="text-slate-300" aria-hidden="true">|</span><Rating value={c.rating} count={c.reviews} /></>}
      </div>
      <div className="mt-5 w-full truncate text-center text-xs text-mut">
        {coverage}
      </div>
    </div>
  </div>;
}

export function CompanyListCard({ c }: { c: Company }) {
  const { t } = useI18n();
  const x = c as any;
  const roles: string[] = Array.isArray(x.businessRoles) ? x.businessRoles : [];
  const chips = roles.length ? roles : c.tags ?? [];
  const meta = [x.subtype, x.primaryIndustry].filter(Boolean).join(" · ");
  return (
    <MarketplaceCard href={`/c/${c.id}`} className="overflow-hidden p-0">
      <FeaturedBadge on={(c as any).featured} />
      <PostCardActions kind="company" post={c} className="absolute right-4 top-4 z-10 h-9 w-9" />
      <Link href={`/c/${c.id}`} aria-label={`View ${c.name}`} className="grid min-w-0 grid-cols-[80px_minmax(0,1fr)] gap-4 p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold sm:grid-cols-[96px_minmax(0,1fr)_150px] sm:items-center sm:gap-5 sm:p-5">
        <CompanyLogo c={c} large />
        <div className="min-w-0">
          <h3 className="min-w-0 truncate pr-10 text-lg font-bold leading-tight text-navy sm:pr-0" title={c.name}>{c.name}</h3>
          <p className="mt-1 truncate text-sm text-mut" title={c.tagline}>{c.tagline}</p>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-mut"><span className="inline-flex items-center gap-1"><LocationPin className="hidden h-3.5 w-3.5 shrink-0 sm:inline" />{c.city}</span>{c.reviews > 0 && <Rating value={c.rating} count={c.reviews} />}</div>
          {meta && <p className="mt-2 hidden truncate text-xs text-navy/70 sm:block" title={meta}>{meta}</p>}
          <div className="mt-3 hidden sm:block"><JobseekerListSkillChips skills={chips} /></div>
        </div>
        <div className="hidden items-end justify-between gap-3 border-t border-line pt-3 sm:col-span-1 sm:flex sm:min-h-24 sm:flex-col sm:items-end sm:justify-end sm:border-t-0 sm:py-1 sm:pl-4">
          <span className="text-xs text-mut">{x.serviceCoverage || (c.since ? `${t("cards.since")} ${c.since}` : "")}</span>
          <span className="text-xs font-semibold text-navy">{t("cards.viewCompany")} →</span>
        </div>
      </Link>
    </MarketplaceCard>
  );
}

type JobApplicationStatus = "loading" | "unavailable" | "applied" | "shortlisted" | "accepted" | "rejected" | "withdrawn";

type JobCardListing = PostOwnership & Pick<Job, "id" | "title" | "company" | "city" | "salary" | "type" | "posted" | "applicants" | "tags"> & {
  createdAt?: string; coverImage?: string; postedBy?: string; identityVerified?: boolean; verifiedProfessions?: string[];
};

function JobPosterLine({ j, className = "" }: { j: JobCardListing; className?: string }) {
  return <div className={className}>
    {j.postingIdentity?.kind !== "business" && j.company !== j.postedBy && <p className="truncate text-sm text-mut" title={j.company}>{j.company}</p>}
    {j.postedBy && <ListingAuthorLine postingIdentity={j.postingIdentity} name={j.postedBy} identityVerified={j.identityVerified}
      verifiedProfessions={j.verifiedProfessions} className="text-sm" />}
  </div>;
}

export function JobCard({ j, applicationStatus, list = false, showMemberActions = true, footerAccessory }: { j: JobCardListing; applicationStatus?: JobApplicationStatus; list?: boolean } & CardPresentation) {
  const { t } = useI18n();
  const cover = (j as any).coverImage as string | undefined;
  const hasActiveApplication = applicationStatus === "applied" || applicationStatus === "shortlisted" || applicationStatus === "accepted";
  const statusLabel: Partial<Record<JobApplicationStatus, string>> = {
    applied: t("cards.applied"),
    shortlisted: t("cards.shortlisted"),
    accepted: t("cards.hired"),
  };
  if (list) return (
    <MarketplaceCard href={`/j/${j.id}`} className="overflow-hidden p-0">
      <FeaturedBadge on={(j as any).featured} />
      {showMemberActions && <PostCardActions kind="job" post={j} className="absolute right-4 top-4 z-10 h-9 w-9" />}
      <div className="grid min-w-0 grid-cols-[80px_minmax(0,1fr)] gap-4 p-4 sm:grid-cols-[96px_minmax(0,1fr)_150px] sm:items-center sm:gap-5 sm:p-5">
        <div className="h-20 w-20 overflow-hidden rounded-xl bg-navy-50 sm:h-24 sm:w-24"><CoverArt src={cover} alt="" Icon={Briefcase} /></div>
        <div className="min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold">
        <div className="min-w-0">
          <h3 className="truncate pr-10 text-lg font-bold leading-tight text-navy sm:pr-0" title={j.title} aria-label={j.title}><Link href={`/j/${j.id}`} aria-label={`${t("discovery.viewDetails")}: ${j.title}`} className="hover:underline">{j.title}</Link></h3>
          <JobPosterLine j={j} className="mt-1" />
          <div className="mt-2 flex min-w-0 items-center gap-1.5 text-xs leading-5 text-mut"><LocationPin className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 truncate" title={cityFromListingLocation(j.city)}>{cityFromListingLocation(j.city)}</span></div>
          <div className="mt-3 flex items-end justify-between gap-2 sm:hidden">
            <div className="min-w-0"><span className="text-sm font-bold text-navy">{formatCompactCardPhpText(j.salary)}</span><span className="mt-1 block truncate text-xs text-mut">{j.applicants} {t("cards.applicants")}</span></div>
            {showMemberActions && (applicationStatus === "loading" ? <span className="text-xs text-mut">{t("cards.checking")}</span> : applicationStatus === "unavailable" ? <span className="text-xs text-mut">{t("cards.unableToVerify")}</span> : hasActiveApplication ? <span className="text-xs font-semibold text-brand-greendark">{statusLabel[applicationStatus]}</span> : <span className="text-xs font-semibold text-brand-reddark">{t("cards.applyNow")} →</span>)}
          </div>
          <div className="mt-3 hidden sm:block"><JobseekerListSkillChips skills={j.tags ?? []} /></div>
        </div>
        </div>
        <div className="hidden items-end justify-between gap-3 border-t border-line pt-3 sm:col-span-1 sm:flex sm:min-h-24 sm:flex-col sm:items-end sm:justify-end sm:border-t-0 sm:py-1 sm:pl-4">
          <div className="min-w-0"><span className="text-sm font-bold text-navy"><span className="sm:hidden">{formatCompactCardPhpText(j.salary)}</span><span className="hidden sm:inline">{formatCompactPhpText(j.salary)}</span></span><span className="mt-1 block text-xs text-mut">{j.applicants} {t("cards.applicants")}</span></div>
          {showMemberActions && (applicationStatus === "loading" ? <span className="text-xs text-mut">{t("cards.checking")}</span> : applicationStatus === "unavailable" ? <span className="text-xs text-mut">{t("cards.unableToVerifyApplication")}</span> : hasActiveApplication ? <span className="text-xs font-semibold text-brand-greendark">{statusLabel[applicationStatus]}</span> : <span className="text-xs font-semibold text-brand-reddark">{t("cards.applyNow")} →</span>)}
        </div>
      </div>
    </MarketplaceCard>
  );
  return (
    <MarketplaceCard href={`/j/${j.id}`} className="p-4 flex flex-col gap-3">
      <FeaturedBadge on={(j as any).featured} />
      {showMemberActions && <PostCardActions kind="job" post={j} imageOverlay className={saveChip} />}
      <CardCover href={`/j/${j.id}`} src={cover} alt={j.title} Icon={Briefcase} imageClassName="aspect-[16/10] sm:aspect-[4/3]" />
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <Link href={`/j/${j.id}`} className="block min-w-0 truncate whitespace-nowrap pr-10 font-bold text-navy hover:text-brand-red" title={j.title}>{j.title}</Link>
          <JobPosterLine j={j} />
          <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs leading-5 text-mut"><LocationPin className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 truncate" title={cityFromListingLocation(j.city)}>{cityFromListingLocation(j.city)}</span></p>
        </div>
      </div>
      <div className="hidden sm:block"><CardTagRow tags={j.tags ?? []} /></div>
      <div className={`mt-auto flex gap-2 border-t-0 pt-0 sm:flex-row sm:items-center sm:justify-between sm:gap-0 sm:border-t sm:border-line sm:pt-1 ${footerAccessory ? "flex-row items-center justify-between" : "flex-col"}`}>
        <div className={footerAccessory ? "min-w-0" : undefined}>
          <div className={`text-sm font-bold text-navy ${footerAccessory ? "break-words" : ""}`}><span className="sm:hidden">{formatCompactCardPhpText(j.salary)}</span><span className="hidden sm:inline">{formatCompactPhpText(j.salary)}</span></div>
          <span className="hidden text-xs text-mut items-center gap-1 sm:flex"><Users className="w-3 h-3" />{j.applicants} {t("cards.applicants")} · {formatRelativeTime(j.createdAt, j.posted)}</span>
        </div>
        {showMemberActions && (applicationStatus === "loading" ? (
          <span className="chip h-9 px-4 text-xs bg-navy-50 text-mut">{t("cards.checkingApplication")}</span>
        ) : applicationStatus === "unavailable" ? (
          <span className="chip h-9 px-4 text-xs bg-navy-50 text-mut">{t("cards.unableToVerifyApplication")}</span>
        ) : hasActiveApplication ? (
          <span className={`chip h-9 px-4 text-xs ${applicationStatus === "accepted" ? "bg-emerald-100 text-emerald-700" : "bg-navy-50 text-navy"}`}>{statusLabel[applicationStatus]}</span>
        ) : (
          <Link href={`/j/${j.id}`} className="btn-red hidden h-9 px-4 text-xs sm:inline-flex">{t("cards.applyNow")}</Link>
        ))}
        {footerAccessory}
      </div>
    </MarketplaceCard>
  );
}

type ProjectCardListing = PostOwnership & Pick<Project, "id" | "title" | "location" | "deadline" | "bids"> & {
  budget?: number | null; createdAt?: string; posted?: string;
  coverImage?: string; postedBy?: string; identityVerified?: boolean; verifiedProfessions?: string[];
};

export function ProjectCard({ p, list = false, showMemberActions = true, footerAccessory }: { p: ProjectCardListing; list?: boolean } & CardPresentation) {
  const { t } = useI18n();
  const { user } = useAuth();
  // [projects.public-privacy](../../../docs/product/projects.md#projects-public-privacy): keep a cached owner's estimate hidden when the signed-in account changes.
  const privateEstimate = user?.id !== undefined && p.ownerUserId === user.id && typeof p.budget === "number" && p.budget > 0 ? p.budget : null;
  const summary = <div className="min-w-0"><span className="text-xs text-mut">{privateEstimate ? t("cards.privateEstimate", "Your estimate (private)") : t("bid.posted")}</span><div className="text-sm font-bold text-navy">{privateEstimate ? (list ? peso(privateEstimate) : formatCompactCardPhp(privateEstimate)) : formatRelativeTime(p.createdAt, p.posted || "—")}</div></div>;
  const cover = (p as any).coverImage as string | undefined;
  const featured = "featured" in p && p.featured === true;
  // [projects.bidding-deadline](../../../docs/product/projects.md#projects-bidding-deadline): legacy "Open" and unusable dates have no bidding deadline to display.
  const biddingDeadline = p.deadline?.trim() || "";
  const hasBiddingDeadline = biddingDeadline.toLowerCase() !== "open" && !Number.isNaN(Date.parse(biddingDeadline));
  if (list) return (
    <MarketplaceCard href={`/project/${p.id}`} className="overflow-hidden p-0">
      <FeaturedBadge on={(p as any).featured} />
      {showMemberActions && <PostCardActions kind="project" post={p} className="absolute right-4 top-4 z-10 h-9 w-9" />}
      <div className="grid min-w-0 grid-cols-[64px_minmax(0,1fr)] gap-3 p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold sm:grid-cols-[96px_minmax(0,1fr)_150px] sm:items-center sm:gap-5 sm:p-5">
        <div className="relative h-16 w-16 overflow-hidden rounded-xl bg-navy-50 sm:h-24 sm:w-24"><CoverArt src={cover} alt="" Icon={Gavel} /><span className="chip absolute right-0.5 top-0.5 bg-brand-gold/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand-gold sm:hidden">{p.bids} {t("cards.bids")}</span></div>
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2 pr-10 sm:pr-0"><h3 className="min-w-0 truncate text-lg font-bold leading-tight text-navy" title={p.title} aria-label={p.title}><Link href={`/project/${p.id}`} aria-label={`${t("discovery.viewDetails")}: ${p.title}`} className="hover:underline">{p.title}</Link></h3><span className="chip hidden shrink-0 bg-brand-gold/15 text-brand-gold sm:inline-flex">{p.bids} {t("cards.bids")}</span></div>
          {p.postedBy && <ListingAuthorLine postingIdentity={p.postingIdentity} name={p.postedBy} identityVerified={p.identityVerified}
            verifiedProfessions={p.verifiedProfessions} className="mt-1 text-sm" />}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-mut"><span className="inline-flex min-w-0 items-center gap-1 truncate"><LocationPin className="hidden h-3.5 w-3.5 shrink-0 sm:inline" />{cityFromListingLocation(p.location)}</span>{hasBiddingDeadline && <span className="hidden items-center gap-1 sm:inline-flex"><Clock className="h-3.5 w-3.5" />{t("cards.deadline")} {biddingDeadline}</span>}</div>
        </div>
        <div className="col-span-2 mt-1 flex items-center justify-between gap-2 border-t border-line pt-2 sm:hidden">
          {summary}
          {showMemberActions && <span className="shrink-0 text-xs font-semibold text-navy">{t("cards.submitBid")} →</span>}
        </div>
        <div className="hidden items-end justify-between gap-3 border-t border-line pt-3 sm:col-span-1 sm:flex sm:min-h-24 sm:flex-col sm:items-end sm:justify-end sm:border-t-0 sm:py-1 sm:pl-4">
          {summary}
          {showMemberActions && <span className="text-xs font-semibold text-navy">{t("cards.submitBid")} →</span>}
        </div>
      </div>
    </MarketplaceCard>
  );
  return (
    <MarketplaceCard href={`/project/${p.id}`} className="p-4 flex flex-col gap-2 sm:gap-3">
      <FeaturedBadge on={(p as any).featured} />
      {showMemberActions && <PostCardActions kind="project" post={p} imageOverlay className="absolute right-3 top-3 z-10 h-8 w-8" />}
      <CardCover href={`/project/${p.id}`} src={cover} alt={p.title} Icon={Gavel} imageClassName="aspect-[16/10] sm:aspect-[4/3]" />
      <span className={`chip absolute left-3 z-10 bg-brand-gold/15 text-brand-gold sm:hidden ${featured ? "top-12" : "top-3"}`}>{p.bids} {t("cards.bids")}</span>
      <div className="flex min-w-0 items-start justify-between gap-2 pr-0 sm:pr-8">
        <div className="min-w-0 flex-1">
          <span className="chip hidden bg-brand-gold/15 text-brand-gold mb-1 sm:inline-flex">{p.bids} {t("cards.bids")}</span>
          <h3 className="min-w-0 truncate whitespace-nowrap pr-0 font-bold text-navy sm:pr-10" title={p.title} aria-label={p.title}><Link href={`/project/${p.id}`} aria-label={`${t("discovery.viewDetails")}: ${p.title}`} className="hover:underline">{p.title}</Link></h3>
        </div>
      </div>
      {p.postedBy && <ListingAuthorLine postingIdentity={p.postingIdentity} name={p.postedBy} identityVerified={p.identityVerified}
        verifiedProfessions={p.verifiedProfessions} className="text-sm" />}
      <p className="text-xs text-mut flex items-center gap-1 sm:hidden">{cityFromListingLocation(p.location)}</p>
      <p className="hidden text-xs text-mut items-center gap-1 sm:flex"><LocationPin className="w-3 h-3 shrink-0" />{cityFromListingLocation(p.location)}</p>
      {hasBiddingDeadline && <div className="hidden items-center gap-4 text-xs text-mut sm:flex">
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{t("cards.deadline")} {biddingDeadline}</span>
      </div>}
      <div className={`mt-auto flex gap-2 pt-2 border-t border-line sm:flex-row sm:items-center sm:justify-between sm:gap-0 sm:pt-1 ${footerAccessory ? "flex-row items-center justify-between" : "flex-col"}`}>
        {summary}
        {showMemberActions && <Link href={`/project/${p.id}`} className="btn-red hidden h-9 px-4 text-xs sm:inline-flex"><Gavel className="w-3.5 h-3.5" />{t("cards.submitBid")}</Link>}
        {footerAccessory}
      </div>
    </MarketplaceCard>
  );
}

export function RfqCard({ r, list = false, showMemberActions = true, footerAccessory }: { r: PostOwnership & Pick<Rfq, "id" | "title" | "category" | "location" | "deadline" | "quotes" | "items"> & { detail?: Rfq["detail"]; coverImage?: string }; list?: boolean } & CardPresentation) {
  const { t } = useI18n();
  const items = Array.isArray(r.items) ? r.items : [];
  // [rfq.request-type](../../../docs/product/rfqs.md#rfq-request-type): omit empty Services counts while keeping historical line items readable.
  const showLineItems = r.detail?.requestType !== "services" || items.length > 0;
  return (
    <MarketplaceCard href={`/rfq/${r.id}`} className="overflow-hidden p-0 sm:flex sm:flex-col sm:gap-3 sm:p-4">
      {showMemberActions && <PostCardActions kind="rfq" post={r} imageOverlay className={saveChip} />}
      <Link href={`/rfq/${r.id}`} className={`block sm:-mx-4 sm:-mt-4 ${list ? "hidden sm:block" : ""}`}>
        <div className={`${list ? "aspect-[4/3]" : "aspect-[16/10] sm:aspect-[4/3]"} overflow-hidden`}><RfqCover src={r.coverImage} alt={r.title} /></div>
      </Link>
      {list ? (
        <div className="grid min-w-0 grid-cols-[80px_minmax(0,1fr)] gap-4 p-4 sm:hidden">
          <div className="h-20 w-20 overflow-hidden rounded-xl"><RfqCover src={r.coverImage} alt={r.title} /></div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 pr-10">
              <h3 className="min-w-0 truncate text-sm font-bold text-navy" title={r.title}>{r.title}</h3>
            </div>
            <RfqTypeBadge requestType={r.detail?.requestType} />
            <p className="mt-1 truncate text-xs text-mut">{r.location} · {r.category}</p>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-mut">
              <span>{r.quotes} {r.quotes === 1 ? t("cards.quote") : t("cards.quotes")}</span>
              <span>{t("cards.due")} {r.deadline}</span>
            </div>
            <div className="mt-3 flex items-center justify-between gap-2">
              {showLineItems && <span className="truncate text-xs text-mut">{items.length} {items.length === 1 ? t("cards.lineItem") : t("cards.lineItems")}</span>}
              {showMemberActions && <span className="shrink-0 text-xs font-semibold text-navy">{r.detail?.requestType === "services" ? t("cards.submitQuote") : t("cards.viewRequest", "View request")} →</span>}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-w-0 flex-col gap-3 p-4 sm:hidden">
          <div className="min-w-0">
            <h3 className="truncate font-bold text-navy" title={r.title}>{r.title}</h3>
            <p className="truncate text-sm text-mut" data-testid="rfq-type">{t(rfqTypeLabelKey(r.detail?.requestType))}</p>
            <p className="mt-0.5 truncate text-xs text-mut" title={r.location}>{cityFromListingLocation(r.location)}</p>
          </div>
          <p className="mt-auto break-words text-xs font-semibold text-navy">{t("cards.due")} {r.deadline}</p>
          {footerAccessory && <div className="flex justify-end border-t border-line pt-2">{footerAccessory}</div>}
        </div>
      )}
      <div className="hidden flex-1 flex-col gap-3 sm:flex">
        <div className="flex min-w-0 items-start justify-between gap-2 pr-8">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap gap-1"><RfqTypeBadge requestType={r.detail?.requestType} /><span className="chip bg-brand-green/10 text-brand-greendark">{r.quotes} {r.quotes === 1 ? t("cards.quote") : t("cards.quotes")}</span></div>
            <h3 className="min-w-0 truncate whitespace-nowrap font-bold text-navy" title={r.title}>{r.title}</h3>
          </div>
          <ReceiptText className="h-5 w-5 shrink-0 text-navy" />
        </div>
        <p className="flex min-w-0 items-center gap-1 truncate text-xs text-mut" title={`${r.location} · ${r.category}`}><LocationPin className="hidden h-3 w-3 shrink-0 sm:block" />{r.location} · {r.category}</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-mut">
          {showLineItems && <span className="flex items-center gap-1"><Package className="h-3 w-3" />{items.length} {items.length === 1 ? t("cards.lineItem") : t("cards.lineItems")}</span>}
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{t("cards.due")} {r.deadline}</span>
        </div>
        <div className="mt-auto flex items-center justify-end gap-2 border-t border-line pt-2">
          {list && showLineItems && <span className="min-w-0 truncate text-xs text-mut" title={items.slice(0, 2).map((i) => i.name).join(", ")}>{items.slice(0, 2).map((i) => i.name).join(", ")}{items.length > 2 ? "…" : ""}</span>}
          {showMemberActions && <Link href={`/rfq/${r.id}`} className={`${list ? "btn-navy" : "btn-red"} ml-auto h-9 shrink-0 px-4 text-xs`}><ReceiptText className="h-3.5 w-3.5" />{r.detail?.requestType === "services" ? t("cards.submitQuote") : t("cards.viewRequest", "View request")}</Link>}
          {footerAccessory}
        </div>
      </div>
    </MarketplaceCard>
  );
}

export function ItemCard({ it, list = false, typeLabel, showMemberActions = true, footerAccessory }: { it: any; list?: boolean; typeLabel?: string } & CardPresentation) {
  const { t } = useI18n();
  const isRent = it.mode === "rent";
  const isEquip = it.kind === "equipment";
  const isProperty = it.kind === "property";
  const isBusiness = it.kind === "business"; // [#5]
  const kindLabel = typeLabel || (it.itemType ? itemPostingLabel(it.itemType, it.kind) : t(`detail.item.kind.${it.kind}`, itemTypeLabel(it.kind)));
  const KindIcon = isProperty || isBusiness ? Building2 : isEquip ? Wrench : Boxes;
  const tags = Array.isArray(it.tags) ? it.tags : [];
  const inStock = (it.stock ?? 0) > 0;
  const stockUnit = isRent ? t("cards.available", "available") : saleUnitLabel(it.unit || "", String(it.stock ?? 0));
  if (list) return (
    <MarketplaceCard href={`/item/${it.id}`} className="overflow-hidden p-0">
      {showMemberActions && <PostCardActions kind="item" post={it} className="absolute right-4 top-4 z-10 h-9 w-9" />}
      <div className="grid min-w-0 grid-cols-[64px_minmax(0,1fr)] gap-3 p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold sm:grid-cols-[96px_minmax(0,1fr)_150px] sm:items-center sm:gap-5 sm:p-5">
        <div className="h-16 w-16 overflow-hidden rounded-xl bg-navy-50 sm:h-24 sm:w-24"><CoverArt src={it.imageUrl || it.coverImage} alt="" Icon={KindIcon} /></div>
        <div className="min-w-0">
          <div className="hidden flex-wrap items-center gap-1.5 sm:flex"><span className={`chip ${isProperty || isBusiness ? "bg-brand-green/10 text-brand-greendark" : isEquip ? "bg-brand-gold/15 text-brand-gold" : "bg-navy-50 text-navy-600"}`}><KindIcon className="w-3 h-3" />{kindLabel}</span>{(isBusiness || isRent) && <span className="chip bg-navy-50 text-navy-600">{isBusiness ? t("detail.item.forSale") : t("detail.item.forRent")}</span>}{it.condition && <span className="chip bg-navy-50 text-navy-600 capitalize">{it.condition === "used" ? t("detail.item.used") : it.condition === "new" ? t("detail.item.new") : it.condition}</span>}</div>
          <h3 className="truncate pr-10 text-sm font-bold leading-tight text-navy sm:mt-1 sm:text-lg sm:pr-0" title={it.title} aria-label={it.title}><Link href={`/item/${it.id}`} aria-label={`${t("discovery.viewDetails")}: ${it.title}`} className="hover:underline">{it.title}</Link></h3>
          {it.postedBy && <ListingAuthorLine postingIdentity={it.postingIdentity} name={it.postedBy} identityVerified={it.identityVerified}
            verifiedProfessions={it.verifiedProfessions} className="mt-1 text-xs" />}
          <div className="mt-1 flex min-w-0 items-center gap-2 text-xs leading-5 text-mut sm:mt-2"><span className="inline-flex min-w-0 flex-1 items-center gap-1.5"><LocationPin className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 truncate" title={it.location || undefined}>{it.location || "—"}</span></span>{it.postingIdentity?.kind !== "business" && it.sellerRating && <CompactCardRating value={it.sellerRating.average} count={it.sellerRating.count} />}</div>
          <div className="mt-3 hidden sm:block"><JobseekerListSkillChips skills={tags} /></div>
        </div>
        <div className="col-span-2 mt-1 flex items-center justify-between gap-3 border-t border-line pt-2 sm:col-span-1 sm:mt-0 sm:min-h-24 sm:flex-col sm:items-end sm:justify-end sm:border-t-0 sm:py-1 sm:pl-4">
          <div className={`flex min-w-0 gap-2 sm:text-right ${isBusiness ? "flex-col items-start gap-1 sm:items-end" : "items-baseline sm:justify-end"}`}><span className="whitespace-nowrap text-sm font-bold text-navy"><span className="sm:hidden">{formatCompactCardPhp(it.price)}</span><span className="hidden sm:inline">{peso(it.price)}</span>{isRent ? `/${it.unit || t("detail.item.day")}` : ""}</span>{!isProperty && <span className={`truncate text-[10px] sm:text-xs ${isBusiness ? "text-mut" : inStock ? "text-brand-green" : "text-brand-red"}`}>{isBusiness ? t("detail.item.askingPrice") : inStock ? `${it.stock} ${stockUnit || t("cards.inStock")}` : t("detail.item.outOfStock")}</span>}</div>
          {showMemberActions && <span className="text-xs font-semibold text-navy">{isBusiness ? t("detail.item.inquire") : isRent ? t("cards.rent") : t("cards.buy")} →</span>}
        </div>
      </div>
    </MarketplaceCard>
  );
  // [items.discovery-and-stock](../../../docs/product/items.md#items-discovery-and-stock): ordinary grid cards put only the stored status over the photo;
  // list chips stay intact, and missing values never imply an item condition.
  const statusLabel = isBusiness
    ? it.businessOperatingStatus && t(`detail.item.operatingStatus.${it.businessOperatingStatus}`, BUSINESS_OPERATING_STATUSES.find((status) => status.value === it.businessOperatingStatus)?.label || it.businessOperatingStatus)
    : it.condition && (it.condition === "used" ? t("detail.item.used") : it.condition === "new" ? t("detail.item.new") : it.condition);
  return (
    <MarketplaceCard href={`/item/${it.id}`} className="overflow-hidden flex flex-col">
      {showMemberActions && <PostCardActions kind="item" post={it} imageOverlay className={saveChip} />}
      <Link href={`/item/${it.id}`} className="block">
        <div className="relative aspect-[16/10] bg-navy-50 overflow-hidden sm:aspect-[4/3]">
          <CoverArt src={it.imageUrl || it.coverImage} alt={it.title} Icon={isProperty || isBusiness ? Building2 : isEquip ? Wrench : Package} />
          {statusLabel && <span title={statusLabel} className="chip absolute left-3 top-3 max-w-[calc(100%-64px)] break-words bg-white/95 text-navy shadow-sm">{statusLabel}</span>}
        </div>
      </Link>
      <div className="p-3 flex flex-col gap-2 flex-1 sm:p-4">
        <Link href={`/item/${it.id}`} className="block min-w-0 truncate whitespace-nowrap pr-0 font-bold leading-snug text-navy hover:text-brand-red sm:pr-10" title={it.title}>{it.title}</Link>
        {it.postedBy && <ListingAuthorLine postingIdentity={it.postingIdentity} name={it.postedBy} identityVerified={it.identityVerified}
          verifiedProfessions={it.verifiedProfessions} />}
        <div className="flex min-w-0 items-center gap-2 text-xs leading-5 text-mut">
          <span className="inline-flex min-w-0 flex-1 items-center gap-1.5"><LocationPin className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 truncate" title={it.location || undefined}>{it.location || "—"}</span></span>
          {it.postingIdentity?.kind !== "business" && it.sellerRating && <CompactCardRating value={it.sellerRating.average} count={it.sellerRating.count} />}
        </div>
        <div className="hidden sm:block"><CardTagRow tags={tags} /></div>
        <div className={`flex gap-2 pt-2 mt-auto border-t border-line sm:flex-row sm:items-center sm:justify-between sm:gap-2 ${footerAccessory ? "flex-row items-center justify-between" : "flex-col items-stretch"}`}>
          <div className={`flex min-w-0 gap-2 ${isBusiness ? "flex-col items-start gap-1" : footerAccessory ? "flex-wrap items-baseline" : "items-baseline"}`}>
            <div className="whitespace-nowrap text-sm font-bold text-navy"><span className="sm:hidden">{formatCompactCardPhp(it.price)}{isRent ? `/${it.unit || t("detail.item.day")}` : ""}</span><span className="hidden sm:inline">{peso(it.price)}{isRent ? <span className="text-xs font-normal text-mut">/{it.unit || t("detail.item.day")}</span> : ""}</span></div>
            {isBusiness
              ? <span className="text-[11px] text-mut">{t("detail.item.askingPrice")}</span>
              : !isProperty && <span className={`truncate text-[10px] sm:text-[11px] ${inStock ? "text-brand-green" : "text-brand-red"}`}>{inStock ? `${it.stock} ${stockUnit || t("cards.inStock")}` : t("detail.item.outOfStock")}</span>}
          </div>
          {showMemberActions && <Link href={`/item/${it.id}`} className={`btn-red h-9 shrink-0 ${isBusiness ? "w-auto self-end px-2 text-[11px] sm:self-auto" : "w-full px-3 text-xs sm:w-auto sm:px-4"}`}>{isBusiness ? t("detail.item.inquire") : <><Package className="w-3.5 h-3.5" />{isRent ? t("cards.rent") : t("cards.buy")}</>}</Link>}
          {footerAccessory}
        </div>
      </div>
    </MarketplaceCard>
  );
}
